import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';

@Component({
  standalone: true,
  imports: [UiModule],
  selector: 'app-expandable-content-header',
  template: `
    <div
      [class]="headerClasses"
      [class.mr-mid]="true"
    >
      <ng-content></ng-content>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableContentHeaderComponent implements OnInit, OnChanges {
  @Input() size: TextSize = TextSize.MID;
  @Input() sizeTablet: TextSize = TextSize.MID;

  headerClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes || 'sizeTablet' in changes) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.headerClasses = textVariants({
      color: FontColor.LIGHT,
      size: this.size,
      sizeTablet: this.sizeTablet,
      weight: FontWeight.BOLD,
    });
  }
}

@Component({
  standalone: true,
  imports: [TrackingModule, UiModule],
  selector: 'app-expandable-content',
  template: `
    <div
      class="Card-header"
      [class.Card-header-borderRadius]="hasBorderRadius"
      tabindex="0"
      [flTrackingLabel]="trackingLabel"
      (click)="handleClick()"
      (keydown.enter)="handleClick()"
    >
      <ng-content select="app-expandable-content-header"></ng-content>
      <fl-icon
        class="ExpandIcon"
        [class.IsExpanded]="expanded"
        [color]="IconColor.LIGHT"
        [name]="'ui-chevron-down'"
        [size]="IconSize.MID"
        [useIconFont]="true"
        [fillIconFont]="true"
        [weight]="FontWeight.MEDIUM"
      ></fl-icon>
    </div>
    <div
      class="Card-body"
      [class.Card-body-padding]="hasBodyPadding"
      [class.Card-isExpanded]="expanded"
      [class.Card-isHidden]="!expanded"
    >
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['./expandable-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableContentComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  FontWeight = FontWeight;
  expanded = false;

  @Input() hasBodyPadding = false;
  @Input() hasBorderRadius = false;
  @Input() trackingLabel: string;

  handleClick(): void {
    this.expanded = !this.expanded;
  }
}
